// ============================================================================
// Estimation de la place occupée par le recto d'une carte 63 × 88 mm
// (gabarit de conception 74 × 103,37 mm, cf. card-face.css).
// ============================================================================
// Heuristique en « lignes » : zone utile ≈ 64 × 90 mm, corps 7,5 pt ⇒ ~40
// caractères par ligne, ~28 lignes. Sert d'indicateur dans l'éditeur et de
// garde de publication (`overflow` bloque). L'aperçu à l'échelle réelle reste
// le juge final — cette estimation évite juste les cartes manifestement trop
// chargées.
// ============================================================================

import type { CardContent, CardSection } from './schemas';

export const CARD_CHARS_PER_LINE = 40;
/** Au-delà : la carte est dense mais passe encore. */
export const CARD_LINES_COMFORT = 23;
/** Au-delà : la carte déborde. */
export const CARD_LINES_MAX = 27;
/** Hauteur approximative d'une figure (≈ 30 mm). */
export const CARD_VISUAL_LINES = 9;

export type CardFit = 'ok' | 'tight' | 'overflow';

/** Longueur « visible » approximative : retire la syntaxe markdown/LaTeX la plus courante. */
export function visibleLength(text: string): number {
  return text
    .replace(/\\(frac|dfrac|sqrt|times|cdot|left|right|quad|text|mathrm|mathbb)/g, '')
    .replace(/[\\${}*_`#]/g, '')
    .trim().length;
}

function lines(text: string | undefined, charsPerLine = CARD_CHARS_PER_LINE): number {
  if (!text) return 0;
  return text
    .split('\n')
    .reduce((acc, part) => acc + Math.max(1, Math.ceil(visibleLength(part) / charsPerLine)), 0);
}

export function sectionLines(section: CardSection): number {
  const heading = 'title' in section && section.title ? 1 : 0;
  const gap = 0.5;
  switch (section.kind) {
    case 'steps':
      // Retrait du numéro : un peu moins de caractères par ligne.
      return heading + gap + section.items.reduce((a, it) => a + lines(it, 36), 0);
    case 'formula':
      return (
        heading + gap + 2 + Math.ceil(section.variables.length / 2) + lines(section.conditions)
      );
    case 'table': {
      const cols = section.headers.length;
      const perCell = Math.max(8, Math.floor(CARD_CHARS_PER_LINE / cols));
      const rowLines = (row: string[]) =>
        Math.max(1, ...row.map((c) => Math.ceil(visibleLength(c) / perCell)));
      return (
        heading +
        gap +
        rowLines(section.headers) +
        section.rows.reduce((a, r) => a + rowLines(r), 0)
      );
    }
    case 'markdown':
      return heading + gap + lines(section.md);
    case 'example':
      return (
        1 +
        gap +
        lines(section.statement) +
        section.steps.reduce((a, s) => a + lines(s), 0) +
        lines(section.result)
      );
    case 'when_to_use':
    case 'pitfall':
      return 1 + gap + lines(section.md);
    case 'visual':
      return gap + CARD_VISUAL_LINES + lines(section.caption);
  }
}

/** Lignes estimées du recto, titre de la carte inclus (2 lignes). */
export function estimateCardLines(title: string, content: CardContent): number {
  const titleLines = Math.max(1, Math.ceil(visibleLength(title) / 28)) + 0.5;
  return titleLines + content.sections.reduce((acc, s) => acc + sectionLines(s), 0);
}

export function estimateCardFit(title: string, content: CardContent): CardFit {
  const total = estimateCardLines(title, content);
  if (total > CARD_LINES_MAX) return 'overflow';
  if (total > CARD_LINES_COMFORT) return 'tight';
  return 'ok';
}
